import { EventTypes } from '@event/types';
import { SvgIconProps } from '@mui/material';
import { MessageDescriptor } from 'react-intl';

export type SearchHit = {
    id?: string | number;
    title?: string;
    name?: string;
    url?: string;
    path?: string;
    link?: string;
    suffix?: string;
    category?: string;
    external_id?: string;
} & Partial<Record<`title_${string}`, string>>;

export type EventHit = Pick<SearchHit, 'id' | 'name' | 'url'> & {
    country_code?: string;
    type?: string;
    banner_image?: string;
    language_code?: string;
};

export type DocumentHit = {
    id: number;
    name_translation: string;
    document_type: string;
    products: string;
    product_group: string;
    /**
     * The document name translations keyed by language code.
     */
    document_name: Record<string, string>;
    /**
     * The document URL translations keyed by language code.
     */
    url: Record<string, string>;
    /**
     * The document file type translations keyed by language code.
     */
    file_type: Record<string, string>;
};

export type DropboxHit = {
    id: string;
    name: string;
    suffix: string;
    type: string;
    size: number;
    path: string;
    file_type: string;
    last_edited_at: string;
    download_path: string;
    download_url_json_path: string;
};

export type LanguageTitleFields = Partial<Record<`title_${string}`, string>>;

export type OnlineTrainingHit = Pick<SearchHit, 'id' | 'title' | 'url'> &
    LanguageTitleFields & {
        category?: string;
    };

export type VideoHit = Pick<SearchHit, 'id' | 'title'> & { external_id?: string };

export type SearchCategoryUrl<T extends SearchHit | DocumentHit = SearchHit | DocumentHit> = {
    getHref: (item: T) => string;
    getTarget?: (item: T) => React.HTMLAttributeAnchorTarget | undefined;
    target?: React.HTMLAttributeAnchorTarget;
};

export type SearchCategory<T extends SearchHit | DocumentHit = SearchHit | DocumentHit> = {
    name: MessageDescriptor;
    icon: React.FC<SvgIconProps>;
    index: string;
    scope?: string;
    in: boolean;
    url: SearchCategoryUrl<T>;
    downloadable?: boolean;
    /**
     * Determines the display order of the category across all search sources, heavier weights are sorted lower in the list.
     */
    weight: number;
    chip?: (item: T) => React.ReactNode;
    title?: (item: T) => string;
};

export type ResultItemUrl = {
    href: string;
    target?: React.HTMLAttributeAnchorTarget;
};

export type ResultItem = {
    title: string;
    url: string | ResultItemUrl;
    type?: string;
};

export type EventResultItem = ResultItem & {
    country_code?: string;
    name?: string;
    banner_image?: string;
    language_code?: string;
    type?: EventTypes;
};

export type ResultsSectionProps<T extends ResultItem> = {
    Icon: React.FC<SvgIconProps>;
    name: string;
    search: string;
    results: T[];
    totalResultsCount?: number;
    onShowNext?: (newHitsPerPage: number) => void;
    loading?: boolean;
    urlIn?: boolean;
    showNext?: boolean;
    downloadable?: boolean;
    chip?: (item: T) => React.ReactNode;
    title?: (item: T) => string;
    hitsPerPage?: number;
    isSkuResults?: boolean;
};

export type SkuInformation = {
    sku: string;
    created_at: string;
    html_description: string | null;
    is_eol: boolean;
};

export type SkuResource = {
    id: number;
    name: string;
    document_type: string;
    url: string;
    notes: string;
    sku: string[];
};

export type SkuQrImageFileType = 'png' | 'svg';

export type SkuQrImages = {
    [key in SkuQrImageFileType]: {
        black_on_white: string;
        white_on_black: string;
        white_on_blue: string;
        white_on_transparent: string;
    };
};

export type SkuPMSTechnicalData = {
    sku: string;
    field_name: string;
    field_value: string;
};

export type SkuSearchResultsType = {
    id: number;
    name: string;
    short_name: string;
    category: string;
    description: string;
    modified_at: string;
    image: string;
    sku: string[];
    sku_information: SkuInformation[];
    documents: SkuResource[];
    external_images: SkuResource[];
    main_images: SkuResource[];
    qr_images: SkuQrImages;
    pms_technical_data: SkuPMSTechnicalData[];
};

export type SkuSearchResultsDocumentTypeConfig = {
    type: SkuSearchResultsDocumentTypes;
    icon: React.FC<SvgIconProps>;
    name: MessageDescriptor;
};

export enum SkuSearchResultsDocumentTypes {
    DATASHEET = 'Datasheet',
    PRODUCT_MANUAL = 'Product Manual',
}

export type SkuSearchResultsTransformed = Omit<SkuSearchResultsType, 'documents'> & {
    documents: Partial<Record<SkuSearchResultsDocumentTypes, ResultItem[]>>;
};
